import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Drawer, Box, Typography, TextField, Button, List, ListItemButton, ListItemText, CircularProgress, Alert } from '@mui/material'
import useIsMobile from '../../hooks/useIsMobile'
import { getAttempts } from './photoReviewApi'

// Mirrors the server's MIN_OVERRIDE_PROMPT_LENGTH; the server still validates.
const MIN_PROMPT_LENGTH = 10

export default function PromptDrawer({ open, photoId, shotLabel, fetchResource, onClose, onSend }) {
    const { t, i18n } = useTranslation()
    const isMobile = useIsMobile()
    const [attempts, setAttempts] = useState(null)
    const [prompt, setPrompt] = useState('')
    const [loadFailed, setLoadFailed] = useState(false)

    useEffect(() => {
        if (!open) return
        let cancelled = false
        setAttempts(null)
        setLoadFailed(false)
        getAttempts(fetchResource, photoId).then(response => {
            if (cancelled) return
            if (response.error) {
                setLoadFailed(true)
                return
            }
            const list = response.attempts || []
            setAttempts(list)
            setPrompt(list.length ? list[list.length - 1].prompt : '')
        })
        return () => { cancelled = true }
    }, [open, photoId, fetchResource])

    const trimmed = prompt.trim()
    const formatDate = iso => new Date(iso).toLocaleString(i18n.language)

    return (
        <Drawer anchor='right' open={open} onClose={onClose} PaperProps={{ sx: { width: isMobile ? '100%' : 520, p: 2 } }}>
            <Typography variant='h6' sx={{ mb: 1 }}>{t('photoReview.prompt.title', { label: shotLabel })}</Typography>
            {loadFailed && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
                    <Alert severity='error'>{t('photoReview.prompt.loadFailed')}</Alert>
                    <Button onClick={onClose}>{t('photoReview.prompt.cancel')}</Button>
                </Box>
            )}
            {loadFailed ? null : attempts === null ? <CircularProgress /> : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {attempts.length === 0 && <Alert severity='info'>{t('photoReview.prompt.noEarlier')}</Alert>}
                    <TextField
                        label={t('photoReview.prompt.label')}
                        value={prompt}
                        onChange={e => setPrompt(e.target.value)}
                        multiline
                        minRows={10}
                        InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                        helperText={trimmed.length < MIN_PROMPT_LENGTH ? t('photoReview.prompt.tooShort') : ' '}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant='contained' disabled={trimmed.length < MIN_PROMPT_LENGTH} onClick={() => { onSend(trimmed); onClose() }}>
                            {t('photoReview.prompt.send')}
                        </Button>
                        <Button onClick={onClose}>{t('photoReview.prompt.cancel')}</Button>
                    </Box>
                    {attempts.length > 0 && (
                        <Box>
                            <Typography variant='subtitle2'>{t('photoReview.prompt.history')}</Typography>
                            <List dense>
                                {[...attempts].reverse().map(attempt => (
                                    <ListItemButton key={attempt.attemptNo} onClick={() => setPrompt(attempt.prompt)}>
                                        <ListItemText primary={t('photoReview.prompt.attempt', { n: attempt.attemptNo, model: attempt.model, date: formatDate(attempt.createdAt) })} />
                                    </ListItemButton>
                                ))}
                            </List>
                        </Box>
                    )}
                </Box>
            )}
        </Drawer>
    )
}
